import type { ComponentPropsWithoutRef, JSX, MouseEventHandler, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon, type IconName } from '@/components/ui/icon';
import { EntityCardHeader } from './entity-card-header';

export interface EditableEntityCardProps extends Omit<ComponentPropsWithoutRef<typeof Card>, 'id'> {
  title: string;
  icon?: IconName;
  subtitle?: ReactNode;
  id?: string | null;
  idLabel?: string | null;
  onEdit?: MouseEventHandler<HTMLButtonElement>;
  editLabel?: string;
  footer?: ReactNode;
}

export function EditableEntityCard({
  title,
  icon,
  subtitle,
  id,
  idLabel,
  onEdit,
  editLabel = 'Modifica',
  className,
  children,
  footer,
  ...cardProps
}: EditableEntityCardProps): JSX.Element {
  return (
    <Card
      className={cn(
        'flex h-full flex-col rounded-lg border border-[var(--border-default)] bg-surface shadow-sm',
        className
      )}
      {...cardProps}
    >
      <div className="flex h-full flex-col gap-4 p-6">
        <EntityCardHeader
          title={title}
          icon={icon}
          subtitle={subtitle}
          id={id}
          idLabel={idLabel}
          trailingContent={
            onEdit ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={onEdit}
                className="flex items-center gap-1 text-text-secondary hover:text-text-primary"
              >
                <Icon name="edit" className="text-base" aria-hidden="true" />
                <span className="sr-only">{editLabel}</span>
              </Button>
            ) : null
          }
        />
        <div className="flex flex-1 flex-col gap-3 text-sm text-text-primary">{children}</div>
        {footer ? <div className="pt-2">{footer}</div> : null}
      </div>
    </Card>
  );
}
